import React, { useEffect, useMemo, useState } from "react";
import useListCompany from "../../../../../hooks/useListCompany";
import { useSelectedCompany } from "../../../../../hooks/useSelectedCompany";
import { getFetcher, getPdfUrl } from "../../../../../services/api";
import useSWR from "swr";
import { Button, Card, CardBody, CardHeader, Divider, Spinner, TableCell, TableRow } from "@nextui-org/react";
import ModalFinalizeReport from "./ModalFinalizeReport";
import { format } from "date-fns";
import ModalReportReview from "./ModalReportReview";
import FilterReactSelect from "../../../../elements/FilterReactSelect";
import ClientTablePagination from "../../../../elements/ClientTablePagination";
import useAuth from "../../../../../hooks/useAuth";
import RolesAccess from "../../../../../enums/roles";
import { formattedDate, groupedTransportDataByVehicle, hasValidRole, hasValidStatus } from "../../../../../services/helpers";
import { ArrowDownTrayIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import ModalDetailPreviewTransport from "./ModalDetailPreviewTransport";
import StatusPengajuanPelaporan from "../../../../../enums/statusPengajuanPelaporan";

export default function TableReportTransportPreview({
  isPreviewOnly = false,
  selectedCompany: companyProp,
  setSelectedCompany: setCompanyProp,
  selectedPeriod: periodProp,
  setSelectedPeriod: setPeriodProp,
}) {
  const { roles } = useAuth();

  // Komponen ini dipakai di empat halaman. Hanya tab Pengangkutan yang
  // mengoper pilihan dari induknya supaya perusahaan dan periode tetap sama
  // saat berpindah tab; halaman lain tetap memakai state sendiri.
  // Perusahaan bawaan mengikuti pengguna yang login (null untuk super admin),
  // sejalan dengan TableReportPreview di tab sebelahnya. Sebelumnya useState(null)
  // sehingga tab Pengangkutan mulai kosong sementara tab Pengelolaan sudah terisi.
  const [localCompany, setLocalCompany] = useSelectedCompany();
  const [localPeriod, setLocalPeriod] = useState(null);
  const terkendali = typeof setCompanyProp === "function" && typeof setPeriodProp === "function";

  const selectedCompany = terkendali ? companyProp : localCompany;
  const setSelectedCompany = terkendali ? setCompanyProp : setLocalCompany;
  const selectedPeriod = terkendali ? periodProp : localPeriod;
  const setSelectedPeriod = terkendali ? setPeriodProp : setLocalPeriod;
  const { data, isLoading, mutate } = useSWR(selectedCompany && selectedPeriod ? `/api/period/preview-pelaporan-pengangkutan?companyId=${selectedCompany.value}&periodId=${selectedPeriod.value}` : null, getFetcher);
  // Konteks pengangkutan: daftar perusahaan disaring ke tipe pengangkut.
  // Tanpa filter, endpoint ini mengembalikan seluruh 8.585 perusahaan
  // (10,2 MB, ~2,5 detik) hanya untuk mengisi satu dropdown.
  const { dataCompany, isLoadingCompany } = useListCompany({ tipePerusahaan: ["Mengangkut B3"], hasIdCompanySimpel: true });
  const { data: dataPeriod, isLoading: isLoadingPeriod } = useSWR(`/api/period/all`, getFetcher);
  const groupedDataVehicle = data ? groupedTransportDataByVehicle(data, isLoading) : [];

  // Pesan "tidak ada data" dari backend ditampilkan di sini, bukan di dalam
  // groupedTransportDataByVehicle yang berjalan saat render.
  useEffect(() => {
    if (data?.message) toast.error(data.message);
  }, [data?.message]);

  useEffect(() => {
    // Saat dikendalikan induk, periode sudah ditentukan di sana - menimpanya di
    // sini justru membuat pilihan pengguna melompat balik ke periode aktif.
    if (terkendali) return;
    if (dataPeriod) {
      const activePeriod = dataPeriod.find((item) => item?.isActive === true);
      if (activePeriod) {
        setSelectedPeriod({ label: activePeriod.name, value: activePeriod.id });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataPeriod, terkendali]);

  // Helper function untuk mengambil data unik
  const getUniqueB3Names = (bahanB3Array) => {
    const uniqueNames = [...new Set(bahanB3Array.map((item) => item.b3Substance.name.trim()))];
    return uniqueNames.join(", ");
  };

  const getUniqueAsalMuat = (bahanB3Array) => {
    const allAsalMuat = bahanB3Array.flatMap((item) => item.asalMuat || []);
    const uniqueAsalMuat = [...new Set(allAsalMuat.map((item) => item.namaPerusahaan))];
    return uniqueAsalMuat.join(", ");
  };

  const getUniqueTujuanBongkar = (bahanB3Array) => {
    const allTujuanBongkar = bahanB3Array.flatMap((item) => item.asalMuat?.flatMap((asal) => asal.tujuanBongkar || []) || []);
    const uniqueTujuanBongkar = [...new Set(allTujuanBongkar.map((item) => item.namaPerusahaan))];
    return uniqueTujuanBongkar.join(", ");
  };

  const header = ["Perusahaan", "Kendaraan", "Nama B3", "Total Angkut (KG)", "Periode", "Perusahaan Asal Muat", "Tujuan Bongkar", "Keterangan", "Aksi"];

  const content = (item, index) => (
    <TableRow key={index}>
      <TableCell>{data?.company?.name || "-"}</TableCell>
      <TableCell>
        {item.vehicle.noPolisi} - {item.vehicle.modelKendaraan}
      </TableCell>
      <TableCell>{item.bahanB3 && item.bahanB3.length > 0 ? getUniqueB3Names(item.bahanB3) : "-"}</TableCell>
      <TableCell>{item.totalJumlahB3 || item.vehicle.totalB3 || 0} Kg</TableCell>
      <TableCell>{data?.period?.name || "-"}</TableCell>
      <TableCell>{item.bahanB3 && item.bahanB3.length > 0 ? getUniqueAsalMuat(item.bahanB3) || "-" : "-"}</TableCell>
      <TableCell>{item.bahanB3 && item.bahanB3.length > 0 ? getUniqueTujuanBongkar(item.bahanB3) || "-" : "-"}</TableCell>
      <TableCell>{item.bahanB3 && item.bahanB3.length > 0 ? `Melakukan ${item.bahanB3.length}x Pengangkutan` : "Tidak ada pengangkutan"}</TableCell>
      <TableCell>
        <ModalDetailPreviewTransport item={item.bahanB3} />
      </TableCell>
    </TableRow>
  );

  async function openPdfDirectly(endpoint) {
    try {
      await getPdfUrl(endpoint);
    } catch (error) {
      toast.error("Gagal buka PDF");
    }
  }

  return (
    <>
      {!isPreviewOnly && (
        <div className="w-full flex items-center justify-end gap-2">
          {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PENGELOLA]) && hasValidStatus(data?.preview?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, null, undefined]) && (
            <ModalFinalizeReport
              endpoint="/api/period/finalize-pelaporan-pengangkutan"
              mutate={mutate}
              selectedCompany={selectedCompany}
              selectedPeriod={selectedPeriod}
              setSelectedCompany={setSelectedCompany}
              setSelectedPeriod={setSelectedPeriod}
            />
          )}
          {hasValidRole(roles, [RolesAccess.SUPER_ADMIN, RolesAccess.PIC_PELAPORAN]) && hasValidStatus(data?.preview?.statusPelaporan, [StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN]) && (
            <ModalReportReview endpoint="/api/period/review-pelaporan-pengangkutan" />
          )}
        </div>
      )}
      {isLoading ? (
        <div className="flex items-center justify-center mt-3">
          <Spinner />
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-3 mt-3">
          <div className="col-span-1 flex flex-col gap-3">
            <Card radius="sm" className="col-span-1">
              <CardHeader>Perusahaan</CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-3">
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Nama Perusahaan</span>
                  <span className="text-sm font-medium">{data?.company?.name || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Alamat</span>
                  <span className="text-sm font-medium">{data?.company?.alamatKantor || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Tipe Perusahaan</span>
                  <span className="text-sm font-medium">{data?.company?.tipePerusahaan?.map((item) => item?.name).join(", ") || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Email Perusahaan</span>
                  <span className="text-sm font-medium">{data?.company?.emailKantor || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Fax Perusahaan</span>
                  <span className="text-sm font-medium">{data?.company?.faxKantor || "-"}</span>
                </div>
              </CardBody>
            </Card>
            <Card radius="sm" className="col-span-1">
              <CardHeader>Periode</CardHeader>
              <Divider />
              <CardBody className="flex flex-col gap-3">
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Periode</span>
                  <span className="text-sm font-medium">{data?.period?.name || "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Tanggal Mulai Periode</span>
                  <span className="text-sm font-medium">{data?.period?.startPeriodDate ? formattedDate(data?.period?.startPeriodDate) : "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Tanggal Akhir Periode</span>
                  <span className="text-sm font-medium">{data?.period?.endReportingDate ? formattedDate(data?.period?.endPeriodDate) : "-"}</span>
                </div>
                <div className="flex flex-col">
                  <span className="text-xs text-ink-400 uppercase">Tanggal Akhir Finalisasi</span>
                  <span className="text-sm font-medium">{data?.period?.finalizationDeadline ? formattedDate(data?.period?.finalizationDeadline) : "-"}</span>
                </div>
              </CardBody>
            </Card>
          </div>
          <Card radius="sm" className="col-span-3">
            <CardHeader>Preview Laporan Pengangkutan</CardHeader>
            <Divider />
            <CardBody>
              <div className="w-full flex justify-end items-center gap-2 mb-3">
                <div className="w-2/4 flex flex-col justify-end">
                  <div className="flex items-center gap-2">
                    {selectedCompany && selectedPeriod && hasValidStatus(data?.preview?.statusPelaporan, [StatusPengajuanPelaporan.DRAFT, StatusPengajuanPelaporan.MENUNGGU_PERSETUJUAN, null, undefined]) ? (
                      <Button size="sm" color="primary" onPress={() => openPdfDirectly(`/api/pdf/generateBuktiLaporPengangkutan/${selectedCompany.value}/${selectedPeriod.value}`)} startContent={<ArrowDownTrayIcon className="size-4" />}>
                        Bukti Laporan
                      </Button>
                    ) : (
                      ""
                    )}
                    {selectedCompany && selectedPeriod && data?.preview?.statusPelaporan === StatusPengajuanPelaporan.DISETUJUI ? (
                      <Button size="sm" color="primary" startContent={<ArrowDownTrayIcon className="size-4" />} onPress={() => openPdfDirectly(`/api/pdf/generateTandaTerimaPengangkutan/${selectedCompany.value}/${selectedPeriod.value}`)}>
                        Tanda Terima
                      </Button>
                    ) : (
                      ""
                    )}
                  </div>
                </div>
                <div className="w-1/4">
                  <FilterReactSelect
                    defaultValue={selectedCompany}
                    placeholder="Cari Perusahaan"
                    options={dataCompany?.data?.map((item) => ({ label: item.name, value: item.id }))}
                    isLoading={isLoadingCompany}
                    setValue={setSelectedCompany}
                  />
                </div>
                <div className="w-1/4">
                  <FilterReactSelect defaultValue={selectedPeriod} placeholder="Cari Periode" options={dataPeriod?.map((item) => ({ label: item.name, value: item.id }))} isLoading={isLoadingPeriod} setValue={setSelectedPeriod} />
                </div>
              </div>
              <ClientTablePagination header={header} content={content} isLoading={isLoading} data={groupedDataVehicle} />
            </CardBody>
          </Card>
        </div>
      )}
    </>
  );
}
